import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useCartSync } from "@/hooks/useCartSync";
import { NavigationBlockerProvider } from "@/hooks/useNavigationBlocker";
import Navbar from "@/components/Navbar";
import OnboardingFlow from "@/components/OnboardingFlow";
import MilestoneCelebration from "@/components/MilestoneCelebration";
import InstallAppBanner from "@/components/InstallAppBanner";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const Index = lazy(() => import("./pages/Index"));
const Programa = lazy(() => import("./pages/Programa"));
const TuPlan = lazy(() => import("./pages/TuPlan"));
const InfoPage = lazy(() => import("./pages/InfoPage"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const CoachTraining = lazy(() => import("./pages/CoachTraining"));
const Recetas = lazy(() => import("./pages/Recetas"));
const Coach = lazy(() => import("./pages/Coach"));
const CoachPro = lazy(() => import("./pages/CoachPro"));
const Recursos = lazy(() => import("./pages/Recursos"));
const Carrito = lazy(() => import("./pages/Carrito"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));

const ADMIN_EMAILS = ["sanapilates.sn@gmail.com"];

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  const { hasCompletedOnboarding, isLoading: onboardingLoading, isError, refetch, saveOnboarding } = useOnboarding(user?.id);
  const isAdminUser = Boolean(user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));
  const location = useLocation();

  useCartSync();

  if (loading || onboardingLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="ml-3 text-muted-foreground">Cargando tu programa...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="max-w-sm space-y-4 text-center">
          <p className="text-lg font-semibold text-foreground">Hubo un problema de conexión</p>
          <p className="text-muted-foreground">No pudimos cargar tus datos. Tocá el botón para reintentar.</p>
          <Button onClick={() => refetch()} className="h-12 px-8">
            <RefreshCw className="mr-2 h-5 w-5" /> Reintentar
          </Button>
        </div>
      </div>
    );
  }

  if (!hasCompletedOnboarding) {
    return (
      <OnboardingFlow
        onComplete={(data) => saveOnboarding.mutate(data)}
        isSubmitting={saveOnboarding.isPending}
      />
    );
  }

  return (
    <NavigationBlockerProvider>
      <div className="min-h-screen bg-transparent">
        <MilestoneCelebration />
        <Navbar user={user}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/programa" element={<Programa />} />
            <Route path="/tu-plan" element={<TuPlan />} />
            <Route path="/info" element={<InfoPage />} />
            <Route path="/usuarios" element={isAdminUser ? <Usuarios /> : <Navigate to="/" replace />} />
            <Route path="/coach-training" element={isAdminUser ? <CoachTraining /> : <Navigate to="/" replace />} />
            <Route path="/recetas" element={<Recetas />} />
            <Route path="/detox" element={<Navigate to="/recetas?tab=detox" replace />} />
            <Route path="/coach" element={<Coach />} />
            <Route path="/coach-pro" element={<CoachPro />} />
            <Route path="/recursos" element={<Recursos />} />
            <Route path="/recursos/:handle" element={<ProductDetail />} />
            <Route path="/carrito" element={<Carrito />} />
            <Route path="/ayuda" element={<FAQ />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Navbar>
      </div>
    </NavigationBlockerProvider>
  );
};

const AuthGuard = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || "/";
  if (loading) return null;
  if (user) return <Navigate to={from} replace />;
  return <Auth />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner position="top-center" />
      <InstallAppBanner />
      <BrowserRouter>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/auth" element={<AuthGuard />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/*" element={<ProtectedLayout />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
